import { useEffect, useMemo, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Search, Plus, X, Tag } from "lucide-react";
import KBContent from "@/components/KBContent";

const DOC_TYPES = ["Troubleshooting", "How-To", "Runbook", "SOP", "Policy", "Guide"];
const catOf = (d) => d.category || d.doc_type || "General";

export default function Docs() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", doc_type: "Troubleshooting", category: "Knowledge Base", tags: "", content: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/docs").then(r => setItems(asArray(r.data))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => Array.from(new Set(items.map(catOf))).sort(), [items]);
  const filtered = items.filter(d =>
    (!cat || catOf(d) === cat) &&
    (!q || `${d.title} ${d.content} ${(d.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase())));

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content are required"); return; }
    setSaving(true);
    try {
      await api.post("/docs", {
        title: form.title.trim(), doc_type: form.doc_type, category: form.category.trim() || "Knowledge Base",
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean), content: form.content,
      });
      toast.success("Article published");
      setForm({ title: "", doc_type: "Troubleshooting", category: "Knowledge Base", tags: "", content: "" });
      setCreating(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Documentation Management</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Knowledge Base ({items.length})</h1>
          <p className="text-sm text-zinc-500 mt-1">Troubleshooting guides, how-tos and runbooks. Click an article to read the full steps.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search the knowledge base" data-testid="kb-search" className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm w-72 focus:outline-none focus:border-blue-500/60" />
          </div>
          <button onClick={() => setCreating(true)} data-testid="kb-new" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-1.5"><Plus size={14} /> New article</button>
        </div>
      </div>

      {/* Category filter */}
      {categories.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setCat("")} className={`text-xs px-3 py-1 rounded-full border ${!cat ? "bg-blue-600/20 border-blue-600/40 text-zinc-100" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>All ({items.length})</button>
          {categories.map(c => (
            <button key={c} onClick={() => setCat(c)} data-testid={`kb-cat-${c}`} className={`text-xs px-3 py-1 rounded-full border ${cat === c ? "bg-blue-600/20 border-blue-600/40 text-zinc-100" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
              {c} ({items.filter(d => catOf(d) === c).length})
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(d => (
          <button key={d.id} onClick={() => setActive(d)} data-testid={`doc-card-${d.id}`} className="text-left bg-zinc-950 border border-zinc-900 rounded-md p-4 hover:border-zinc-700 transition-colors">
            <div className="flex items-center gap-2"><FileText size={14} className="text-blue-400" /><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{catOf(d)} · {d.doc_type} · v{d.version}</div></div>
            <h2 className="font-display text-base tracking-tight mt-1 text-zinc-100">{d.title}</h2>
            <div className="text-xs text-zinc-500 mt-2 line-clamp-2 whitespace-pre-wrap">{String(d.content || "").replace(/[#*]/g, "")}</div>
            <div className="flex flex-wrap gap-1 mt-3">
              {(d.tags || []).slice(0, 4).map(t => <span key={t} className="text-[10px] bg-zinc-900 border border-zinc-800 rounded px-2 py-0.5 font-mono text-zinc-400">{t}</span>)}
            </div>
          </button>
        ))}
        {filtered.length === 0 && <div className="text-sm text-zinc-500 col-span-full py-8 text-center">No articles match. Try another search, or create one.</div>}
      </div>

      {/* Reader */}
      {active && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-6" onClick={() => setActive(null)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-3xl w-full my-8" onClick={e => e.stopPropagation()} data-testid="kb-reader">
            <div className="flex items-start justify-between px-6 py-4 border-b border-zinc-900 sticky top-0 bg-zinc-950 rounded-t-lg">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-blue-400 font-mono">{catOf(active)} · {active.doc_type} · v{active.version}</div>
                <h2 className="font-display text-xl font-semibold tracking-tight mt-0.5">{active.title}</h2>
              </div>
              <button onClick={() => setActive(null)} data-testid="kb-reader-close" className="p-1.5 rounded hover:bg-zinc-900 text-zinc-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5">
              <KBContent text={active.content} />
              {(active.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-6 pt-4 border-t border-zinc-900">
                  <Tag size={13} className="text-zinc-600 mt-0.5" />
                  {active.tags.map(t => <span key={t} className="text-[10px] bg-zinc-900 border border-zinc-800 rounded px-2 py-0.5 font-mono text-zinc-400">{t}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create */}
      {creating && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-6" onClick={() => setCreating(false)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-2xl w-full my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900">
              <h2 className="font-display text-lg font-semibold">New knowledge base article</h2>
              <button onClick={() => setCreating(false)} className="p-1.5 rounded hover:bg-zinc-900 text-zinc-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="kb-form-title" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })} data-testid="kb-form-type" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm">
                  {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
                <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="Category" data-testid="kb-form-category" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              </div>
              <input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="Tags (comma-separated)" data-testid="kb-form-tags" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Content — supports ## headings, **bold**, - bullets, and 1. numbered steps" data-testid="kb-form-content" rows={12} className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setCreating(false)} className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm rounded px-4 py-2">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="kb-form-save" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-4 py-2">{saving ? "Publishing…" : "Publish article"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
